import { useSearchParams } from 'react-router-dom';
import {
  TASK_SORT_PARAMS,
  type TaskSortSelection,
  type TaskTabSort,
  buildTaskSortBy,
  isSameTaskSortField,
  isTaskSortOrder,
} from './taskSort';
import { useWriteTaskParams } from './useWriteTaskParams';

/**
 * Owns a Tasks-page tab's sort, kept in the URL like its filters, so each tab
 * sorts independently and refresh or back/forward keeps the order.
 *
 * `urlDefault` is what the URL leaves out. It stays the tab's own default while
 * `sort` swaps for the Resolved-only one, so a choice reads back the same
 * either side of the swap (APP-2631).
 */
export const useTaskSort = (
  sort: TaskTabSort | undefined,
  // No default: one would silently be the Resolved-only default once `sort` swaps.
  urlDefault: TaskTabSort['default'] | undefined,
) => {
  const [searchParams] = useSearchParams();
  const writeParams = useWriteTaskParams();
  const fieldParam =
    searchParams.get(TASK_SORT_PARAMS.field) || urlDefault?.field || null;
  const orderParam = searchParams.get(TASK_SORT_PARAMS.order);
  const offeredField =
    sort?.fields.find((field) => field === fieldParam) ??
    sort?.fields.find((field) => isSameTaskSortField(field, fieldParam));

  // A field this sort doesn't offer takes its order with it.
  const selection: TaskSortSelection | undefined =
    sort &&
    (offeredField && urlDefault
      ? {
          field: offeredField,
          order: isTaskSortOrder(orderParam) ? orderParam : urlDefault.order,
        }
      : sort.default);

  const setSelection = ({ field, order }: TaskSortSelection) => {
    // Re-picking the current option would otherwise still reset the page.
    if (field === selection?.field && order === selection?.order) return;

    // Defaults stay out of the URL, so a shared link only carries a deliberate choice.
    writeParams({
      [TASK_SORT_PARAMS.field]: field === urlDefault?.field ? '' : field,
      [TASK_SORT_PARAMS.order]: order === urlDefault?.order ? '' : order,
    });
  };

  return {
    selection,
    setSelection,
    sortBy: sort && selection && buildTaskSortBy(sort, selection),
  };
};

export type TaskSort = ReturnType<typeof useTaskSort>;
